import React, { useState, useRef } from 'react';
import './EvoMLDeployment.css';
import EvoMLDeploymentEmailGenerator from './EvoMLDeploymentEmailGenerator';
import { exportAsPNG, exportAsPDF } from '../../utils/exportUtils';

const EvoMLDeployment = () => {
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [viewMode, setViewMode] = useState('simple'); // 'simple', 'detailed', or 'email'
  const comparisonTableRef = useRef(null);
  const simpleViewRef = useRef(null);
  
  // Interactive Decision Guide State
  const [decisionCriteria, setDecisionCriteria] = useState({
    teamSize: '',
    privacyLevel: '',
    llmRequirements: '',
    budget: '',
    setupTime: '',
    scalability: ''
  });
  const [recommendedDeployments, setRecommendedDeployments] = useState([]);
  
  const generateSecurityComplianceText = () => {
    let text = 'SECURITY & COMPLIANCE INFORMATION\n';
    text += '=================================\n\n';
    
    text += 'LLM INTEGRATION & DATA PRIVACY\n';
    text += '- Supports all major LLM providers: OpenAI, Azure OpenAI, AWS Bedrock, Google Vertex AI, Anthropic, Cohere\n';
    text += '- Integration via API keys or enterprise gateways\n';
    text += '- No customer data used for model training\n';
    text += '- Each provider follows enterprise-grade data handling policies\n';
    text += '- Integration is modular and configurable based on client preferences\n\n';
    
    text += 'SECURITY, COMPLIANCE & OPERATIONAL READINESS\n';
    text += 'Data Residency:\n';
    text += '- Deployments tailored to meet regional requirements (UK, EU, US, etc.)\n\n';
    
    text += 'Authentication & Access:\n';
    text += '- SSO support via SAML2, OAuth2\n';
    text += '- IP allowlists and/or VPN access\n';
    text += '- Data encryption at rest and in transit\n\n';
    
    text += 'Audit & Observability:\n';
    text += '- Full audit logging\n';
    text += '- Integrations with: Sentry, Grafana & Prometheus, Grafana Loki for distributed log aggregation\n\n';
    
    text += 'Secrets & Configuration Management:\n';
    text += '- Integrated with HashiCorp Vault for secure secret management across environments\n\n';
    
    text += 'ARCHITECTURE\n';
    text += '- All deployments run on Kubernetes\n';
    text += '- Only external connections are to LLM providers and authentication gateways\n\n';
    
    text += 'CONTAINER SECURITY\n';
    text += '- Secure registries (Docker Hub, AWS ECR)\n';
    text += '- Multi-layer vulnerability scanning\n';
    text += '- Only validated images deployed\n\n';
    
    text += 'OPEN SOURCE COMPLIANCE\n';
    text += '- Fully vetted dependencies\n';
    text += '- Only permissive licenses used\n';
    text += '- Complete BOM shared during onboarding\n\n';
    
    text += 'SECURITY UPDATES\n';
    text += '- Regular patches and urgent security releases\n';
    text += '- Update notifications for critical issues\n\n';
    
    text += 'COMPLIANCE STANDARDS\n';
    text += '- SOC 2 Type II (in progress)\n';
    text += '- GDPR compliant\n\n';
    
    text += 'For detailed security documentation or compliance inquiries, please contact our security team.';
    
    return text;
  };
  
  const generateComparisonTableText = () => {
    let text = 'DEPLOYMENT OPTIONS OVERVIEW\n';
    text += '===========================\n\n';
    
    // Header row
    text += 'Deployment Type | Infrastructure | Setup Time | Security | Privacy | Internet Access\n';
    text += '----------------|----------------|------------|----------|---------|----------------\n';
    
    // Data rows
    deploymentOptions.forEach(option => {
      const setupTime = option.setupTime || 'Contact us';
      
      let privacyLevel = 'Standard';
      if (option.privacy.includes('Maximum')) {
        privacyLevel = 'Maximum';
      } else if (option.privacy.includes('High')) {
        privacyLevel = 'High';
      } else if (option.privacy.includes('Medium')) {
        privacyLevel = 'Medium';
      } else if (option.privacy.includes('Limited')) {
        privacyLevel = 'Limited';
      }
      
      let internetRequired = 'Required';
      if (option.internetRequired === false) {
        internetRequired = 'Not Required';
      } else if (option.internetRequired === 'Conditional') {
        internetRequired = 'Conditional';
      }
      
      text += `${option.title} | ${option.machines.type} | ${setupTime} | ${option.security?.level || 'Standard'} | ${privacyLevel} | ${internetRequired}\n`;
    });
    
    text += '\nLLM INTEGRATION & DATA PRIVACY\n';
    text += '==============================\n';
    text += 'Artemis supports integration with all major LLM providers—OpenAI, Azure OpenAI, AWS Bedrock, Google Vertex AI, and others—via API keys or enterprise gateways.\n\n';
    text += 'In all SaaS configurations:\n';
    text += '- No customer data is used for model training\n';
    text += '- Each provider follows enterprise-grade data handling policies\n';
    text += '- Integration is modular and configurable based on client preferences\n';
    
    return text;
  };

  const generateRecommendationsText = () => {
    let text = 'DEPLOYMENT RECOMMENDATIONS\n';
    text += '========================\n\n';
    
    text += 'Based on your requirements:\n';
    text += `- Team Size: ${decisionCriteria.teamSize.replace('small', '1-10 users').replace('medium', '11-50 users').replace('large', '51-200 users').replace('enterprise', '200+ users')}\n`;
    text += `- Privacy Level: ${decisionCriteria.privacyLevel.replace('basic', 'Basic - Standard cloud security').replace('enhanced', 'Enhanced - Dedicated resources').replace('high', 'High - On-premise preferred').replace('maximum', 'Maximum - Air-gapped required')}\n`;
    text += `- LLM Requirements: ${decisionCriteria.llmRequirements.replace('cloud', 'Cloud APIs only').replace('byok', 'Cloud APIs with BYOK').replace('local', 'Local models only').replace('hybrid', 'Both cloud and local')}\n`;
    text += `- Budget: ${decisionCriteria.budget.replace('low', 'Limited - Minimize costs').replace('medium', 'Moderate - Balance cost/features').replace('high', 'Flexible - Performance priority').replace('enterprise', 'Enterprise - Best solution')}\n`;
    text += `- Setup Timeline: ${decisionCriteria.setupTime.replace('immediate', 'Immediate - Same day').replace('quick', 'Quick - Within 2-3 days').replace('standard', 'Standard - Within a week').replace('flexible', 'Flexible - Quality over speed')}\n`;
    text += `- Scalability: ${decisionCriteria.scalability.replace('fixed', 'Fixed - Stable workload').replace('moderate', 'Moderate - Some growth').replace('high', 'High - Rapid scaling needed').replace('global', 'Global - Multi-region scale')}\n`;
    text += '\n';
    
    text += 'TOP RECOMMENDATIONS:\n\n';
    
    recommendedDeployments.forEach((rec, index) => {
      text += `${index + 1}. ${rec.deployment.title} (${rec.score}% Match)\n`;
      text += `   Setup Time: ${rec.setupTime}\n`;
      text += `   Why this fits: ${rec.reasons.join(', ')}\n`;
      text += `   Best for: ${rec.deployment.useCase}\n`;
      text += `   LLM Options: ${rec.deployment.llmIntegration.type}\n`;
      text += `   Privacy Level: ${rec.deployment.privacy}\n`;
      text += `   Infrastructure: ${rec.deployment.machines.type}\n`;
      text += '\n';
    });
    
    text += '\nFor more information or to discuss implementation, please contact our team.';
    
    return text;
  };
  
  const generateRecommendations = () => {
    const scored = deploymentOptions.map(deployment => {
      let score = 0;
      let reasons = [];
      
      // Team Size Scoring
      if (decisionCriteria.teamSize === 'small' && deployment.id.includes('saas-shared')) {
        score += 20;
        reasons.push('Perfect for small teams');
      } else if (decisionCriteria.teamSize === 'medium' && deployment.id.includes('saas-dedicated')) {
        score += 20;
        reasons.push('Ideal for medium teams');
      } else if (decisionCriteria.teamSize === 'large' && (deployment.id.includes('cloud') || deployment.id.includes('k8s'))) {
        score += 20;
        reasons.push('Scales for large teams');
      } else if (decisionCriteria.teamSize === 'enterprise' && deployment.id.includes('cloud-native')) {
        score += 20;
        reasons.push('Enterprise-grade solution');
      }
      
      // Privacy Scoring
      if (decisionCriteria.privacyLevel === 'basic' && deployment.privacy.includes('Limited')) {
        score += 15;
      } else if (decisionCriteria.privacyLevel === 'enhanced' && deployment.privacy.includes('High')) {
        score += 20;
        reasons.push('Enhanced privacy controls');
      } else if (decisionCriteria.privacyLevel === 'high' && deployment.privacy.includes('High')) {
        score += 15;
        reasons.push('Strong privacy features');
      } else if (decisionCriteria.privacyLevel === 'maximum' && deployment.privacy.includes('Maximum')) {
        score += 25;
        reasons.push('Maximum privacy (air-gapped)');
      }
      
      // LLM Requirements Scoring
      if (decisionCriteria.llmRequirements === 'cloud' && deployment.llmIntegration.type.includes('Cloud')) {
        score += 20;
        reasons.push('Full cloud LLM support');
      } else if (decisionCriteria.llmRequirements === 'byok' && deployment.llmIntegration.type.includes('BYOK')) {
        score += 25;
        reasons.push('BYOK supported');
      } else if (decisionCriteria.llmRequirements === 'local' && deployment.llmIntegration.type.includes('Local')) {
        score += 20;
        reasons.push('Local LLM capable');
      } else if (decisionCriteria.llmRequirements === 'hybrid' && deployment.llmIntegration.type.includes('Hybrid')) {
        score += 25;
        reasons.push('Hybrid LLM flexibility');
      }
      
      // Budget Scoring
      const costLevel = deployment.cost.match(/\$/g)?.length || 1;
      if (decisionCriteria.budget === 'low' && costLevel === 1) {
        score += 20;
        reasons.push('Budget-friendly');
      } else if (decisionCriteria.budget === 'medium' && costLevel <= 2) {
        score += 15;
      } else if (decisionCriteria.budget === 'high' && costLevel <= 3) {
        score += 10;
      } else if (decisionCriteria.budget === 'enterprise') {
        score += 5; // Budget not a primary concern
      }
      
      // Setup Time Scoring
      let setupDays = 7;
      if (deployment.complexity === 'None') setupDays = 0;
      else if (deployment.complexity === 'Low') setupDays = 1;
      else if (deployment.complexity === 'Medium') setupDays = 2;
      else if (deployment.complexity === 'High') setupDays = 4;
      else if (deployment.complexity === 'Very High') setupDays = 7;
      
      if (decisionCriteria.setupTime === 'immediate' && setupDays === 0) {
        score += 20;
        reasons.push('Instant setup');
      } else if (decisionCriteria.setupTime === 'quick' && setupDays <= 2) {
        score += 15;
        reasons.push('Quick deployment');
      } else if (decisionCriteria.setupTime === 'standard' && setupDays <= 7) {
        score += 10;
      }
      
      // Scalability Scoring
      const scalabilityMatch = deployment.scalability.match(/\d/);
      const scalabilityScore = scalabilityMatch ? parseInt(scalabilityMatch[0]) : 3;
      
      if (decisionCriteria.scalability === 'fixed' && scalabilityScore <= 3) {
        score += 10;
      } else if (decisionCriteria.scalability === 'moderate' && scalabilityScore >= 3) {
        score += 15;
      } else if (decisionCriteria.scalability === 'high' && scalabilityScore >= 4) {
        score += 20;
        reasons.push('Highly scalable');
      } else if (decisionCriteria.scalability === 'global' && scalabilityScore === 5) {
        score += 25;
        reasons.push('Global scale ready');
      }
      
      // Get setup time string
      let setupTime = deployment.complexity;
      if (deployment.id === 'k8s-offline') {
        setupTime = '1 week';
      } else if (deployment.id === 'cloud-native') {
        setupTime = '2-3 days';
      } else if (deployment.complexity === 'None') {
        setupTime = 'Instant';
      } else if (deployment.complexity === 'Low') {
        setupTime = '< 1 day';
      } else if (deployment.complexity === 'Medium') {
        setupTime = '1-2 days';
      } else if (deployment.complexity === 'High') {
        setupTime = '3-5 days';
      } else if (deployment.complexity === 'Very High') {
        setupTime = '1 week';
      }
      
      return {
        deployment,
        score,
        reasons,
        setupTime
      };
    });
    
    // Sort by score descending
    scored.sort((a, b) => b.score - a.score);
    
    // Take top 3 recommendations
    setRecommendedDeployments(scored.slice(0, 3));
  };

  const deploymentOptions = [
    {
      id: 'saas-shared',
      title: 'SaaS - Shared Environment',
      subtitle: 'Multi-tenant Cloud',
      icon: '☁️',
      color: '#FF6B6B',
      features: [
        'Instant access, no setup',
        'Pay-per-use pricing',
        'Automatic updates',
        'Shared infrastructure'
      ],
      machines: {
        icon: '🌐',
        type: 'No Infrastructure Required',
        specs: 'Turintech manages everything',
        details: 'Shared compute resources with isolation',
        examples: 'app.turintech.ai/your-workspace'
      },
      useCase: 'Quick Start, Small Teams, Proof of Concept',
      complexity: 'None',
      cost: '$ (Usage-based)',
      scalability: 'High (4/5)',
      availability: 'Very High (5/5)',
      privacy: 'High - Enterprise-grade security',
      internetRequired: true,
      setupTime: 'Instant',
      security: {
        level: 'Standard',
        features: [
          'SOC 2 Type II (in progress)',
          'GDPR compliant',
          'Data encryption at rest and in transit',
          'SSO support (SAML2, OAuth2)',
          'Regular security patches',
          'Multi-layer vulnerability scanning',
          'Data isolation between tenants'
        ]
      },
      llmIntegration: {
        type: 'Cloud APIs (BYOK)',
        providers: ['OpenAI', 'AWS Bedrock', 'Google Vertex AI', 'Azure OpenAI', 'OpenRouter', 'Anthropic', 'Cohere'],
        dataPrivacy: 'No customer data used for model training. Each provider follows enterprise-grade data handling policies.',
        customSupport: 'Bring Your Own Key (BYOK) - Use your own API keys or our managed keys'
      }
    },
    {
      id: 'saas-dedicated',
      title: 'SaaS - Dedicated Instance',
      subtitle: 'Isolated Cloud Environment',
      icon: '🏢',
      color: '#4ECDC4',
      features: [
        'Dedicated cloud resources',
        'Custom domain & branding',
        'Enhanced SLAs',
        'Isolated infrastructure'
      ],
      machines: {
        icon: '🔐',
        type: 'Dedicated Cloud Instance',
        specs: 'Isolated compute cluster',
        details: 'Your own app instance on our infrastructure',
        examples: 'your-company.turintech.cloud'
      },
      useCase: 'Enterprise Teams, Compliance Requirements',
      complexity: 'Low',
      cost: '$$$ (Fixed monthly)',
      scalability: 'Very High (5/5)',
      availability: 'Very High (5/5)',
      privacy: 'Maximum - Dedicated isolated resources',
      internetRequired: true,
      setupTime: '< 1 day',
      security: {
        level: 'Enhanced',
        features: [
          'Dedicated infrastructure',
          'IP allowlists and VPN access',
          'SOC 2 Type II (in progress)',
          'GDPR compliant',
          'Data encryption at rest and in transit',
          'SSO support (SAML2, OAuth2)',
          'Full audit logging',
          'Integration with Sentry, Grafana, Prometheus',
          'HashiCorp Vault for secrets management'
        ]
      },
      llmIntegration: {
        type: 'Cloud APIs (BYOK) + Private Endpoints',
        providers: ['OpenAI', 'AWS Bedrock', 'Google Vertex AI', 'Azure OpenAI', 'OpenRouter', 'Anthropic', 'Cohere'],
        dataPrivacy: 'Dedicated instance ensures data isolation. No training on your data.',
        customSupport: 'BYOK + Private VPC endpoints for enhanced security'
      }
    },
    {
      id: 'docker-offline',
      title: 'Docker Offline',
      subtitle: 'Fully Air-gapped',
      icon: '🐳',
      color: '#0db7ed',
      features: [
        'Single-node deployment',
        'Docker Compose orchestration',
        'Pre-loaded container images',
        'Local LLM models'
      ],
      machines: {
        icon: '💻',
        type: 'Single Machine',
        specs: 'Laptop/Desktop/Server',
        details: '16GB RAM, 8 CPU cores minimum',
        examples: 'MacBook Pro, Dell Workstation, Single VM'
      },
      useCase: 'Development, POC, Secure Environments',
      complexity: 'Low',
      cost: '$',
      scalability: 'Limited (1/5)',
      availability: 'Single Node (1/5)',
      privacy: 'Maximum - 100% offline',
      internetRequired: false,
      setupTime: '1-2 days',
      security: {
        level: 'Maximum',
        features: [
          'Complete air-gap isolation',
          'No external network access',
          'Physical security controls',
          'Local authentication only',
          'Full data sovereignty'
        ]
      },
      llmIntegration: {
        type: 'Local + Cloud APIs (Optional)',
        providers: ['Llama 2/3', 'Mistral', 'Falcon', 'Custom models', 'Cloud APIs if connected'],
        dataPrivacy: 'Primarily offline - Cloud APIs available when internet connected',
        customSupport: 'Deploy local models or connect to cloud APIs with BYOK'
      }
    },
    {
      id: 'k8s-offline',
      title: 'Kubernetes Offline',
      subtitle: 'Enterprise Air-gapped',
      icon: '☸️',
      color: '#e74c3c',
      features: [
        'Multi-node HA cluster',
        'Auto-scaling capabilities',
        'GPU node support',
        'Enterprise-grade security'
      ],
      machines: {
        icon: '🖥️',
        type: 'Server Cluster',
        specs: '3+ Master nodes, 5+ Worker nodes',
        details: 'Each node: 32GB RAM, 16 CPU cores',
        examples: 'Dell PowerEdge, HP ProLiant, VMware cluster'
      },
      useCase: 'Production, Regulated Industries',
      complexity: 'Very High',
      cost: '$$$',
      scalability: 'High (4/5)',
      availability: 'Very High (5/5)',
      privacy: 'Maximum - 100% offline',
      internetRequired: false,
      setupTime: '~1 week',
      security: {
        level: 'Maximum',
        features: [
          'Enterprise-grade air-gap',
          'Multi-layer network isolation',
          'Hardware security modules (HSM)',
          'Certificate-based authentication',
          'Audit logging and compliance',
          'Encrypted storage at rest'
        ]
      },
      llmIntegration: {
        type: 'Local + Cloud APIs (Configurable)',
        providers: ['Enterprise Llama', 'Mistral', 'Custom models', 'Cloud APIs via secure gateway'],
        dataPrivacy: 'Air-gapped by default - Cloud APIs through secure proxy if enabled',
        customSupport: 'Multi-GPU clusters + optional secure gateway for cloud LLMs'
      }
    },
    {
      id: 'cloud-native',
      title: 'Cloud-Native Hybrid',
      subtitle: 'On-Premise + Cloud',
      icon: '🏢',
      color: '#00bfa5',
      features: [
        'Service mesh architecture',
        'Hybrid cloud flexibility',
        'API gateway integration',
        'ML pipeline orchestration'
      ],
      machines: {
        icon: '🏗️',
        type: 'Data Center + Cloud',
        specs: 'On-prem servers + Cloud services',
        details: '15+ nodes on-premise, cloud burst capability',
        examples: 'Private data center + AWS/GCP/Azure'
      },
      useCase: 'Large Enterprises, Hybrid Strategy',
      complexity: 'Medium',
      cost: '$$$$',
      scalability: 'Very High (5/5)',
      availability: 'High (4/5)',
      privacy: 'Medium - Hybrid control',
      internetRequired: 'Conditional',
      setupTime: '~1 week',
      security: {
        level: 'High',
        features: [
          'Zero-trust architecture',
          'VPN and private connectivity',
          'Data residency controls',
          'Federated authentication',
          'Advanced threat detection',
          'Selective cloud encryption'
        ]
      },
      llmIntegration: {
        type: 'Hybrid - Local + Cloud',
        providers: ['All cloud providers', 'Local models', 'Custom API gateways'],
        dataPrivacy: 'Flexible - Route sensitive data to local LLMs',
        customSupport: 'Intelligent routing based on data sensitivity'
      }
    },
    {
      id: 'cloud-platform',
      title: 'Cloud Platform',
      subtitle: 'Fully Managed (AWS/Azure/GCP)',
      icon: '☁️',
      color: '#4285f4',
      features: [
        'Serverless architecture',
        'Native AI/ML services',
        'Global auto-scaling',
        'Pay-as-you-go pricing'
      ],
      machines: {
        icon: '☁️',
        type: 'No Machines Required',
        specs: 'Cloud provider manages all infrastructure',
        details: 'Auto-scales from 0 to thousands of instances',
        examples: 'AWS ECS/Lambda, Azure Container Apps, GKE/Cloud Run'
      },
      useCase: 'SaaS, Global Scale, Rapid Growth',
      complexity: 'Medium',
      cost: '$$ (usage-based)',
      scalability: 'Very High (5/5)',
      availability: 'Very High (5/5)',
      privacy: 'Limited - Cloud provider managed',
      internetRequired: true,
      setupTime: '< 1 day',
      security: {
        level: 'High',
        features: [
          'Cloud provider security (AWS/Azure/GCP)',
          'DDoS protection',
          'Automated security patches',
          'Identity and Access Management',
          'Cloud KMS/Key Vault encryption',
          'Security monitoring and compliance tools'
        ]
      },
      llmIntegration: {
        type: 'Fully managed cloud',
        providers: ['AWS Bedrock', 'Azure OpenAI', 'Google Vertex AI', 'OpenAI', 'Anthropic'],
        dataPrivacy: 'Cloud provider compliance. No training on customer data.',
        customSupport: 'Auto-scaling LLM endpoints across all cloud providers'
      }
    }
  ];

  const architectureComponents = {
    'saas-shared': [
      { name: 'Load Balancer', type: 'frontend' },
      { name: 'Multi-tenant API', type: 'api' },
      { name: 'Shared LLM Pool', type: 'llm' },
      { name: 'Multi-tenant DB', type: 'database' },
      { name: 'Shared ML Platform', type: 'serving' },
      { name: 'Object Storage', type: 'storage' }
    ],
    'saas-dedicated': [
      { name: 'Dedicated Load Balancer', type: 'frontend' },
      { name: 'Isolated API Instance', type: 'api' },
      { name: 'Dedicated LLM Resources', type: 'llm' },
      { name: 'Isolated Database', type: 'database' },
      { name: 'Dedicated ML Runtime', type: 'serving' },
      { name: 'Isolated Storage', type: 'storage' }
    ],
    'docker-offline': [
      { name: 'Angular Frontend', type: 'frontend' },
      { name: 'Python API', type: 'api' },
      { name: 'Local LLMs', type: 'llm' },
      { name: 'PostgreSQL/MongoDB', type: 'database' },
      { name: 'Ray Serve', type: 'serving' },
      { name: 'Docker Volumes', type: 'storage' }
    ],
    'k8s-offline': [
      { name: 'NGINX Ingress', type: 'frontend' },
      { name: 'API Pods (HA)', type: 'api' },
      { name: 'GPU LLM Pods', type: 'llm' },
      { name: 'StatefulSets DBs', type: 'database' },
      { name: 'Ray Cluster', type: 'serving' },
      { name: 'Ceph Storage', type: 'storage' }
    ],
    'cloud-native': [
      { name: 'API Gateway', type: 'frontend' },
      { name: 'Microservices', type: 'api' },
      { name: 'vLLM/TGI', type: 'llm' },
      { name: 'Distributed DBs', type: 'database' },
      { name: 'Kubeflow', type: 'serving' },
      { name: 'Hybrid Storage', type: 'storage' }
    ],
    'cloud-platform': [
      { name: 'Cloud CDN/Front Door', type: 'frontend' },
      { name: 'Container Service', type: 'api' },
      { name: 'Cloud AI Services', type: 'llm' },
      { name: 'Managed Database', type: 'database' },
      { name: 'ML Platform', type: 'serving' },
      { name: 'Object Storage', type: 'storage' }
    ]
  };

  const componentTypeColors = {
    frontend: '#ff6b6b',
    api: '#4ecdc4',
    llm: '#9b59b6',
    database: '#3498db',
    serving: '#f39c12',
    storage: '#27ae60'
  };

  // Simple view data
  const simpleViewData = [
    {
      id: 'saas-shared',
      title: 'SaaS - Shared',
      icon: '☁️',
      color: '#FF6B6B',
      machine: '🌐 No Infrastructure',
      privacy: '🌐 Standard Privacy',
      internet: '✅ Internet Required',
      bestFor: 'Quick Start & POC'
    },
    {
      id: 'saas-dedicated',
      title: 'SaaS - Dedicated',
      icon: '🏢',
      color: '#4ECDC4',
      machine: '🔐 Dedicated Instance',
      privacy: '🔐 High Privacy',
      internet: '✅ Internet Required',
      bestFor: 'Enterprise Teams'
    },
    {
      id: 'docker-offline',
      title: 'Docker Offline',
      icon: '🐳',
      color: '#0db7ed',
      machine: '💻 Single Machine',
      privacy: '🔒 Maximum Privacy',
      internet: '❌ No Internet',
      bestFor: 'Development & POC'
    },
    {
      id: 'k8s-offline',
      title: 'Kubernetes Offline',
      icon: '☸️',
      color: '#e74c3c',
      machine: '🖥️ Server Cluster',
      privacy: '🔒 Maximum Privacy',
      internet: '❌ No Internet',
      bestFor: 'Enterprise Production'
    },
    {
      id: 'cloud-native',
      title: 'Cloud-Native Hybrid',
      icon: '🏢',
      color: '#00bfa5',
      machine: '🏗️ Data Center + Cloud',
      privacy: '🔐 High Privacy',
      internet: '⚡ Optional Internet',
      bestFor: 'Large Organizations'
    },
    {
      id: 'gcp-cloud',
      title: 'Google Cloud',
      icon: '☁️',
      color: '#4285f4',
      machine: '☁️ No Machines',
      privacy: '🌐 Standard Privacy',
      internet: '✅ Internet Required',
      bestFor: 'SaaS Products'
    }
  ];

  return (
    <div className="evoml-deployment-container">
      <h1 className="deployment-title">
        <span className="title-icon">🚀</span>
        EvoML & Artemis Deployment Architecture Options
      </h1>
      
      {/* View Mode Toggle */}
      <div className="view-toggle">
        <button 
          className={`toggle-btn ${viewMode === 'simple' ? 'active' : ''}`}
          onClick={() => setViewMode('simple')}
        >
          <span className="toggle-icon">📊</span> Simple View
        </button>
        <button 
          className={`toggle-btn ${viewMode === 'detailed' ? 'active' : ''}`}
          onClick={() => setViewMode('detailed')}
        >
          <span className="toggle-icon">📋</span> Detailed View
        </button>
        <button 
          className={`toggle-btn ${viewMode === 'email' ? 'active' : ''}`}
          onClick={() => setViewMode('email')}
        >
          <span className="toggle-icon">📧</span> Email Generator
        </button>
      </div>

      {/* Simple View */}
      {viewMode === 'simple' && (
        <div className="simple-view" ref={simpleViewRef}>
          <div className="simple-grid">
            {simpleViewData.map((option) => (
              <div 
                key={option.id}
                className="simple-card"
                style={{ borderColor: option.color }}
              >
                <div className="simple-header" style={{ backgroundColor: option.color }}>
                  <span className="simple-icon">{option.icon}</span>
                  <h2>{option.title}</h2>
                </div>
                <div className="simple-body">
                  <div className="simple-item">
                    <span className="item-icon">{option.machine}</span>
                  </div>
                  <div className="simple-item">
                    <span className="item-icon">{option.privacy}</span>
                  </div>
                  <div className="simple-item">
                    <span className="item-icon">{option.internet}</span>
                  </div>
                  <div className="simple-best-for">
                    Best for: <strong>{option.bestFor}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="simple-summary">
            <div className="export-buttons">
              <button 
                className="export-btn"
                onClick={() => exportAsPNG(simpleViewRef.current, 'deployment-options-simple.png')}
              >
                📸 Export View as PNG
              </button>
            </div>
            <h2>🎯 Quick Decision Guide</h2>
            <div className="decision-grid">
              <div className="decision-item">
                <h3>Need Maximum Privacy?</h3>
                <p>→ Choose Docker or Kubernetes Offline</p>
              </div>
              <div className="decision-item">
                <h3>Limited Resources?</h3>
                <p>→ Start with Docker on a single machine</p>
              </div>
              <div className="decision-item">
                <h3>Need to Scale Globally?</h3>
                <p>→ Use Google Cloud Platform</p>
              </div>
              <div className="decision-item">
                <h3>Enterprise with Compliance?</h3>
                <p>→ Consider Cloud-Native Hybrid</p>
              </div>
            </div>
          </div>
          
          {/* Comparison Table in Simple View */}
          <div className="simple-comparison-table">
            <h2>📊 Detailed Comparison Table</h2>
            <div className="table-container">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Deployment Type</th>
                    <th>Infrastructure</th>
                    <th>Setup Time</th>
                    <th>Security</th>
                    <th>Privacy</th>
                    <th>Internet</th>
                    <th>LLM Options</th>
                  </tr>
                </thead>
                <tbody>
                  {deploymentOptions.map(option => {
                    const setupTime = option.setupTime || 'Contact us';
                    
                    let privacyLevel = 'Standard';
                    if (option.privacy.includes('Maximum')) {
                      privacyLevel = 'Maximum';
                    } else if (option.privacy.includes('High')) {
                      privacyLevel = 'High';
                    } else if (option.privacy.includes('Medium')) {
                      privacyLevel = 'Medium';
                    } else if (option.privacy.includes('Limited')) {
                      privacyLevel = 'Limited';
                    }
                    
                    let internetRequired = 'Required';
                    if (option.internetRequired === false) {
                      internetRequired = 'Not Required';
                    } else if (option.internetRequired === 'Conditional') {
                      internetRequired = 'Conditional';
                    }
                    
                    let llmOptions = 'Cloud APIs';
                    if (option.llmIntegration?.type) {
                      if (option.llmIntegration.type.includes('BYOK') && option.llmIntegration.type.includes('Private')) {
                        llmOptions = 'Cloud APIs (BYOK) + Private';
                      } else if (option.llmIntegration.type.includes('BYOK')) {
                        llmOptions = 'Cloud APIs (BYOK)';
                      } else if (option.llmIntegration.type.includes('Local') && option.llmIntegration.type.includes('Cloud')) {
                        llmOptions = 'Hybrid (Local + Cloud)';
                      } else if (option.llmIntegration.type.includes('Hybrid')) {
                        llmOptions = 'Hybrid';
                      } else if (option.llmIntegration.type.includes('Local')) {
                        llmOptions = 'Local Models';
                      }
                    }
                    
                    return (
                      <tr key={option.id}>
                        <td>
                          <span className="option-icon">{option.icon}</span>
                          {option.title}
                        </td>
                        <td>{option.machines.type}</td>
                        <td>{setupTime}</td>
                        <td className={`security-${option.security?.level?.toLowerCase() || 'standard'}`}>
                          {option.security?.level || 'Standard'}
                        </td>
                        <td className={`privacy-${privacyLevel.toLowerCase()}`}>
                          {privacyLevel}
                        </td>
                        <td className={internetRequired === 'No' ? 'no-internet' : 'yes-internet'}>
                          {internetRequired}
                        </td>
                        <td>{llmOptions}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="table-actions">
              <button 
                className="export-btn"
                onClick={(e) => {
                  const text = generateComparisonTableText();
                  navigator.clipboard.writeText(text).then(() => {
                    const btn = e.target;
                    const originalText = btn.textContent;
                    btn.textContent = 'Copied!';
                    setTimeout(() => {
                      btn.textContent = originalText;
                    }, 2000);
                  });
                }}
              >
                📋 Copy as Text
              </button>
              <button 
                className="export-btn"
                onClick={() => {
                  const text = generateComparisonTableText();
                  const blob = new Blob([text], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'deployment-comparison-table.txt';
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                💾 Download as Text
              </button>
              <button 
                className="export-btn"
                onClick={() => {
                  const tableElement = document.querySelector('.simple-comparison-table');
                  exportAsPNG(tableElement, 'deployment-comparison-table.png');
                }}
              >
                📸 Export Table as PNG
              </button>
              <button 
                className="export-btn"
                onClick={() => {
                  const tableElement = document.querySelector('.simple-comparison-table');
                  exportAsPDF(tableElement, 'deployment-comparison-table.pdf');
                }}
              >
                📄 Export Table as PDF
              </button>
            </div>
          </div>
          
          {/* Security and Compliance Information */}
          <div className="security-compliance-info">
            <div className="section-header">
              <h3>🔒 Security & Compliance</h3>
              <div className="section-actions">
                <button 
                  className="action-btn copy-btn"
                  onClick={(e) => {
                    const text = generateSecurityComplianceText();
                    navigator.clipboard.writeText(text).then(() => {
                      const btn = e.target;
                      const originalText = btn.textContent;
                      btn.textContent = 'Copied!';
                      setTimeout(() => {
                        btn.textContent = originalText;
                      }, 2000);
                    });
                  }}
                >
                  📋 Copy
                </button>
                <button 
                  className="action-btn download-btn"
                  onClick={() => {
                    const text = generateSecurityComplianceText();
                    const blob = new Blob([text], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `security-compliance-${new Date().toISOString().split('T')[0]}.txt`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                >
                  📥 Download
                </button>
              </div>
            </div>
            <div className="info-grid">
              <div className="info-card">
                <h4>🐳 Container Security</h4>
                <p>All container images are provided through secure registries (Docker Hub, AWS ECR) and undergo comprehensive security scanning with multiple industry-leading tools to ensure vulnerability-free deployments.</p>
              </div>
              <div className="info-card">
                <h4>📚 Open Source Compliance</h4>
                <p>All open source libraries used are thoroughly vetted for security and license compliance. Only libraries with permissive open source licenses are included. A complete list of dependencies and their licenses is provided during the onboarding process.</p>
              </div>
              <div className="info-card">
                <h4>🛡️ Regular Updates</h4>
                <p>Security patches and updates are regularly applied to all deployment options. Critical security updates are prioritized and communicated immediately to all customers.</p>
              </div>
              <div className="info-card">
                <h4>✅ Compliance Standards</h4>
                <p>We are actively pursuing SOC 2 Type II certification (in progress). Our deployments are designed to meet industry standards including GDPR, HIPAA (for dedicated deployments), and ISO 27001. Compliance documentation is available upon request.</p>
              </div>
              <div className="info-card">
                <h4>🔑 Bring Your Own Key (BYOK)</h4>
                <p>All deployment options support BYOK for LLM providers. Use your own API keys to maintain full control over costs and usage. Even offline deployments can optionally connect to cloud LLMs through secure gateways when needed.</p>
              </div>
            </div>
          </div>
          
          {/* Interactive Decision Guide */}
          <div className="interactive-decision-guide">
            <h2>🎯 Interactive Deployment Advisor</h2>
            <p className="guide-subtitle">Answer a few questions to get personalized deployment recommendations</p>
            
            <div className="decision-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Team Size</label>
                  <select 
                    value={decisionCriteria.teamSize} 
                    onChange={(e) => setDecisionCriteria({...decisionCriteria, teamSize: e.target.value})}
                  >
                    <option value="">Select team size</option>
                    <option value="small">1-10 users</option>
                    <option value="medium">11-50 users</option>
                    <option value="large">51-200 users</option>
                    <option value="enterprise">200+ users</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Privacy Requirements</label>
                  <select 
                    value={decisionCriteria.privacyLevel} 
                    onChange={(e) => setDecisionCriteria({...decisionCriteria, privacyLevel: e.target.value})}
                  >
                    <option value="">Select privacy level</option>
                    <option value="basic">Basic - Standard cloud security</option>
                    <option value="enhanced">Enhanced - Dedicated resources</option>
                    <option value="high">High - On-premise preferred</option>
                    <option value="maximum">Maximum - Air-gapped required</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>LLM Requirements</label>
                  <select 
                    value={decisionCriteria.llmRequirements} 
                    onChange={(e) => setDecisionCriteria({...decisionCriteria, llmRequirements: e.target.value})}
                  >
                    <option value="">Select LLM needs</option>
                    <option value="cloud">Cloud APIs only (OpenAI, etc.)</option>
                    <option value="byok">Cloud APIs with BYOK</option>
                    <option value="local">Local models only</option>
                    <option value="hybrid">Both cloud and local</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Budget Considerations</label>
                  <select 
                    value={decisionCriteria.budget} 
                    onChange={(e) => setDecisionCriteria({...decisionCriteria, budget: e.target.value})}
                  >
                    <option value="">Select budget range</option>
                    <option value="low">Limited - Minimize costs</option>
                    <option value="medium">Moderate - Balance cost/features</option>
                    <option value="high">Flexible - Performance priority</option>
                    <option value="enterprise">Enterprise - Best solution</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Setup Timeline</label>
                  <select 
                    value={decisionCriteria.setupTime} 
                    onChange={(e) => setDecisionCriteria({...decisionCriteria, setupTime: e.target.value})}
                  >
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediate - Same day</option>
                    <option value="quick">Quick - Within 2-3 days</option>
                    <option value="standard">Standard - Within a week</option>
                    <option value="flexible">Flexible - Quality over speed</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Scalability Needs</label>
                  <select 
                    value={decisionCriteria.scalability} 
                    onChange={(e) => setDecisionCriteria({...decisionCriteria, scalability: e.target.value})}
                  >
                    <option value="">Select scalability</option>
                    <option value="fixed">Fixed - Stable workload</option>
                    <option value="moderate">Moderate - Some growth</option>
                    <option value="high">High - Rapid scaling needed</option>
                    <option value="global">Global - Multi-region scale</option>
                  </select>
                </div>
              </div>
              
              <button 
                className="generate-recommendations-btn"
                onClick={generateRecommendations}
                disabled={!Object.values(decisionCriteria).every(val => val !== '')}
              >
                Generate Recommendations
              </button>
            </div>
            
            {recommendedDeployments.length > 0 && (
              <div className="recommendations-results">
                <h3>📊 Recommended Deployments (Ranked by Best Fit)</h3>
                <div className="recommendations-actions">
                  <button 
                    className="action-btn copy-btn"
                    onClick={() => {
                      const text = generateRecommendationsText();
                      navigator.clipboard.writeText(text).then(() => {
                        const btn = document.querySelector('.copy-btn');
                        const originalText = btn.textContent;
                        btn.textContent = 'Copied!';
                        setTimeout(() => {
                          btn.textContent = originalText;
                        }, 2000);
                      });
                    }}
                  >
                    📋 Copy as Text
                  </button>
                  <button 
                    className="action-btn download-btn"
                    onClick={() => {
                      const text = generateRecommendationsText();
                      const blob = new Blob([text], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `deployment-recommendations-${new Date().toISOString().split('T')[0]}.txt`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                  >
                    📥 Download as Text
                  </button>
                </div>
                <div className="recommendations-list">
                  {recommendedDeployments.map((rec, index) => (
                    <div key={rec.deployment.id} className="recommendation-card">
                      <div className="recommendation-rank">#{index + 1}</div>
                      <div className="recommendation-content">
                        <div className="recommendation-header">
                          <span className="rec-icon">{rec.deployment.icon}</span>
                          <h4>{rec.deployment.title}</h4>
                          <span className="match-score">{rec.score}% Match</span>
                        </div>
                        <div className="recommendation-details">
                          <p><strong>Why this fits:</strong> {rec.reasons.join(', ')}</p>
                          <p><strong>Setup time:</strong> {rec.setupTime}</p>
                          <p><strong>Best for:</strong> {rec.deployment.useCase}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detailed View */}
      {viewMode === 'detailed' && (
        <>
          <div className="deployment-grid">
        {deploymentOptions.map((option) => (
          <div
            key={option.id}
            className={`deployment-card ${selectedDeployment === option.id ? 'selected' : ''}`}
            onClick={() => setSelectedDeployment(option.id)}
            style={{ borderColor: option.color }}
          >
            <div className="card-header" style={{ backgroundColor: option.color }}>
              <span className="card-icon">{option.icon}</span>
              <h2>{option.title}</h2>
              <p>{option.subtitle}</p>
            </div>
            
            <div className="card-body">
              <div className="machine-requirements">
                <h3>{option.machines.icon} Machine Requirements</h3>
                <div className="machine-info">
                  <p className="machine-type">{option.machines.type}</p>
                  <p className="machine-specs">{option.machines.specs}</p>
                  <p className="machine-details">{option.machines.details}</p>
                  <p className="machine-examples">Examples: {option.machines.examples}</p>
                </div>
              </div>

              <div className="features-section">
                <h3>Key Features</h3>
                <ul>
                  {option.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
              
              <div className="metrics-section">
                <div className="metric">
                  <span className="metric-label">Use Case:</span>
                  <span className="metric-value">{option.useCase}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Setup Time:</span>
                  <span className="metric-value">{option.setupTime}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Cost:</span>
                  <span className="metric-value">{option.cost}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Scalability:</span>
                  <span className="metric-value">{option.scalability}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Availability:</span>
                  <span className="metric-value">{option.availability}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Privacy:</span>
                  <span className="metric-value">{option.privacy}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Internet Access:</span>
                  <span className="metric-value">{option.internetRequired === false ? 'Not Required' : option.internetRequired === 'Conditional' ? 'Conditional' : 'Required'}</span>
                </div>
              </div>
              
              {option.security && (
                <div className="security-section">
                  <h3>🔒 Security Level: {option.security.level}</h3>
                  <ul className="security-features">
                    {option.security.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {option.llmIntegration && (
                <div className="llm-section">
                  <h3>🤖 LLM Integration</h3>
                  <div className="llm-details">
                    <p><strong>Type:</strong> {option.llmIntegration.type}</p>
                    <p><strong>Supported Providers:</strong></p>
                    <div className="llm-providers">
                      {option.llmIntegration.providers.map((provider, index) => (
                        <span key={index} className="provider-badge">{provider}</span>
                      ))}
                    </div>
                    <p className="data-privacy"><strong>Data Privacy:</strong> {option.llmIntegration.dataPrivacy}</p>
                    <p><strong>Custom Support:</strong> {option.llmIntegration.customSupport}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedDeployment && (
        <div className="architecture-details">
          <h2>Architecture Components</h2>
          <div className="components-grid">
            {architectureComponents[selectedDeployment].map((component, index) => (
              <div
                key={index}
                className="component-block"
                style={{ backgroundColor: componentTypeColors[component.type] }}
              >
                {component.name}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="deployment-comparison" ref={comparisonTableRef}>
        <div className="export-buttons">
          <button 
            className="export-btn"
            onClick={() => exportAsPNG(comparisonTableRef.current, 'deployment-comparison.png')}
          >
            📸 Export as PNG
          </button>
          <button 
            className="export-btn"
            onClick={() => exportAsPDF(comparisonTableRef.current, 'deployment-comparison.pdf')}
          >
            📄 Export as PDF
          </button>
        </div>
        <h2>Quick Comparison</h2>
        <table className="comparison-table">
          <thead>
            <tr>
              <th>Deployment Type</th>
              <th>Infrastructure</th>
              <th>Setup Time</th>
              <th>Privacy Level</th>
              <th>Internet Required</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><span className="table-icon">☁️</span> SaaS - Shared</td>
              <td>🌐 No infrastructure</td>
              <td>Instant</td>
              <td>🌐 Standard (Multi-tenant)</td>
              <td>✅ Yes</td>
            </tr>
            <tr>
              <td><span className="table-icon">🏢</span> SaaS - Dedicated</td>
              <td>🔐 Dedicated instance</td>
              <td>1-2 hours</td>
              <td>🔐 High (Isolated)</td>
              <td>✅ Yes</td>
            </tr>
            <tr>
              <td><span className="table-icon">🐳</span> Docker Offline</td>
              <td>💻 Single laptop/server</td>
              <td>30 minutes</td>
              <td>🔒 Maximum (Air-gapped)</td>
              <td>❌ No</td>
            </tr>
            <tr>
              <td><span className="table-icon">☸️</span> Kubernetes Offline</td>
              <td>🖥️ 8+ servers</td>
              <td>1-2 days</td>
              <td>🔒 Maximum (Air-gapped)</td>
              <td>❌ No</td>
            </tr>
            <tr>
              <td><span className="table-icon">🏢</span> Cloud-Native Hybrid</td>
              <td>🏗️ Data center + Cloud</td>
              <td>1-2 weeks</td>
              <td>🔐 High (You control)</td>
              <td>⚡ Optional</td>
            </tr>
            <tr>
              <td><span className="table-icon">☁️</span> Google Cloud</td>
              <td>☁️ No machines needed</td>
              <td>2-4 hours</td>
              <td>🌐 Standard (Cloud policies)</td>
              <td>✅ Yes</td>
            </tr>
          </tbody>
        </table>
      </div>

          <div className="deployment-footer">
            <p className="footer-note">
              💡 <strong>Pro Tip:</strong> Start with Docker for development, scale to Kubernetes for production, 
              and leverage cloud services for global reach and minimal operations.
            </p>
          </div>
        </>
      )}

      {/* Email Generator View */}
      {viewMode === 'email' && (
        <EvoMLDeploymentEmailGenerator deploymentOptions={deploymentOptions} />
      )}
    </div>
  );
};

export default EvoMLDeployment;